import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

import { emptyApi } from "./emptyApi";

export type WebAuthnCredentialSummary = {
  uuid: string;
  device_name: string | null;
  aaguid: string | null;
  backup_eligible: boolean;
  created_at: string;
  last_used_at: string | null;
};

type WebAuthnLoginResponse = {
  access: string;
  refresh: string;
  [key: string]: unknown;
};

export const webauthnApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    webauthnRegistrationBegin: build.mutation<
      PublicKeyCredentialCreationOptionsJSON,
      void
    >({
      query: () => ({
        url: "/api/inkle/webauthn/registration/begin/",
        method: "POST",
      }),
    }),
    webauthnRegistrationFinish: build.mutation<
      WebAuthnCredentialSummary,
      { credential: RegistrationResponseJSON; device_name?: string }
    >({
      query: (body) => ({
        url: "/api/inkle/webauthn/registration/finish/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WEBAUTHN_CREDENTIALS"],
    }),
    webauthnLoginBegin: build.mutation<
      PublicKeyCredentialRequestOptionsJSON,
      void
    >({
      query: () => ({
        url: "/api/inkle/webauthn/login/begin/",
        method: "POST",
      }),
    }),
    webauthnLoginFinish: build.mutation<
      WebAuthnLoginResponse,
      { credential: AuthenticationResponseJSON; accept_tos?: boolean }
    >({
      query: (body) => ({
        url: "/api/inkle/webauthn/login/finish/",
        method: "POST",
        body,
      }),
    }),
    getWebauthnCredentials: build.query<WebAuthnCredentialSummary[], void>({
      query: () => "/api/inkle/webauthn/credentials/",
      providesTags: ["WEBAUTHN_CREDENTIALS"],
    }),
    renameWebauthnCredential: build.mutation<
      WebAuthnCredentialSummary,
      { uuid: string; device_name: string }
    >({
      query: ({ uuid, device_name }) => ({
        url: `/api/inkle/webauthn/credentials/${uuid}/`,
        method: "PATCH",
        body: { device_name },
      }),
      invalidatesTags: ["WEBAUTHN_CREDENTIALS"],
    }),
    deleteWebauthnCredential: build.mutation<void, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `/api/inkle/webauthn/credentials/${uuid}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["WEBAUTHN_CREDENTIALS"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useWebauthnRegistrationBeginMutation,
  useWebauthnRegistrationFinishMutation,
  useWebauthnLoginBeginMutation,
  useWebauthnLoginFinishMutation,
  useGetWebauthnCredentialsQuery,
  useRenameWebauthnCredentialMutation,
  useDeleteWebauthnCredentialMutation,
} = webauthnApis;
